/**
 * ═════════════════════════════════════════════════════════════════════
 *  DASHBOARD (Inicio) · Rediseño UX de lanzamiento
 * ─────────────────────────────────────────────────────────────────────
 *  Objetivos del rediseño:
 *    1. Responder en < 30 segundos: "¿Qué tengo que hacer hoy?"
 *    2. Jerarquía visual clara: (1) alerta urgente · (2) acciones ·
 *       (3) hoy · (4) esta semana · (5) contexto financiero.
 *    3. Personalización opcional, oculta por defecto.
 *    4. Sin duplicados: cada widget aporta valor único.
 *    5. Vacío inteligente: "estás al día" cuando no hay nada urgente.
 * ═════════════════════════════════════════════════════════════════════
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Calendar, CreditCard, FileText, FileSignature, Inbox,
  PartyPopper, Plus, Sparkles, CheckCircle2, Settings,
  UserCog, DollarSign, ArrowRight, AlertTriangle
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";

const today = () => new Date().toISOString().slice(0, 10);
const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [datasets, setDatasets] = useState({});
  const [loading, setLoading] = useState(true);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [activeWidgets, setActiveWidgets] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [wDispRes, cfgRes] = await Promise.all([
          axios.get(`${API}/erp/dashboard/widgets-disponibles`),
          axios.get(`${API}/erp/dashboard/config`),
        ]);
        setAvailableWidgets(wDispRes.data.widgets || []);
        setActiveWidgets(cfgRes.data.widgets || []);
      } catch { /* silencio */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [statsRes, solRes, cotRes, contRes, evRes, pagosRes, recRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/solicitudes`).catch(() => ({ data: [] })),
          axios.get(`${API}/cotizaciones`).catch(() => ({ data: [] })),
          axios.get(`${API}/contratos`).catch(() => ({ data: [] })),
          axios.get(`${API}/eventos`).catch(() => ({ data: [] })),
          axios.get(`${API}/pagos`).catch(() => ({ data: [] })),
          axios.get(`${API}/recordatorios`).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        const hoy = today();
        const semana = daysFromNow(7);
        const mes = daysFromNow(30);
        setDatasets({
          solicitudes: (solRes.data || []).filter((s) => ["nueva", "contactada"].includes(s.estatus)).slice(0, 6),
          cotizacionesPend: (cotRes.data || []).filter((c) =>
            ["enviada", "aprobada", "anticipo_solicitado", "pendiente_revision"].includes(c.estatus)
          ),
          contratosPorFirmar: (contRes.data || []).filter((c) => !c.firmado),
          eventosHoy: (evRes.data || []).filter((e) => e.fecha === hoy && !["cancelado", "finalizado"].includes(e.estatus)),
          eventosSemana: (evRes.data || []).filter((e) => e.fecha > hoy && e.fecha <= semana && !["cancelado", "finalizado"].includes(e.estatus))
            .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "")),
          eventosMes: (evRes.data || [])
            .filter((e) => e.fecha >= hoy && e.fecha <= mes && !["cancelado", "finalizado"].includes(e.estatus))
            .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "")),
          recordatoriosVencidos: (recRes.data || [])
            .filter((r) => !r.completado && r.fecha < hoy && r.estatus !== "enviado" && r.estatus !== "completado"),
          recordatoriosHoy: (recRes.data || [])
            .filter((r) => !r.completado && r.fecha === hoy && r.estatus !== "enviado" && r.estatus !== "completado"),
        });
      } catch {
        toast.error("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleWidget = useCallback((id) => {
    setActiveWidgets(prev => prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]);
  }, []);

  const guardarConfig = useCallback(async () => {
    try {
      await axios.put(`${API}/erp/dashboard/config`, { widgets: activeWidgets });
      toast.success("Configuración guardada");
      setConfigOpen(false);
    } catch {
      toast.error("No se pudo guardar la configuración");
    }
  }, [activeWidgets]);

  // Métricas urgentes que decidí mostrar SIEMPRE
  const urgentes = useMemo(() => {
    const urg = [];
    if ((datasets.recordatoriosVencidos || []).length > 0)
      urg.push({ tipo: "vencidos", count: datasets.recordatoriosVencidos.length, label: "Recordatorios vencidos", path: "/panel/whatsapp" });
    if ((datasets.solicitudes || []).length > 0)
      urg.push({ tipo: "solicitudes", count: datasets.solicitudes.length, label: "Solicitudes nuevas por atender", path: "/panel/eventos" });
    if ((datasets.cotizacionesPend || []).length > 0)
      urg.push({ tipo: "cotizaciones", count: datasets.cotizacionesPend.length, label: "Cotizaciones esperan tu seguimiento", path: "/panel/pipeline" });
    if ((datasets.contratosPorFirmar || []).length > 0)
      urg.push({ tipo: "contratos", count: datasets.contratosPorFirmar.length, label: "Contratos por firmar", path: "/panel/contratos" });
    return urg;
  }, [datasets]);

  const primerNombre = user?.name?.split(" ")[0] || "bienvenido";

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Acciones rápidas (max 4, contextuales por rol interno) ────
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin" || user?.workspace_rol === "admin";
  const rol = (user?.rol_interno || user?.workspace_rol || "").toLowerCase();
  // Iter124 · P0 · roles solo-lectura-asignada no personalizan ni ven CTAs prohibidos
  const esRolSoloLectura = ["mesero", "chef", "cliente"].includes(rol);

  const accionesRapidas = (() => {
    if (rol === "chef" || rol === "mesero") {
      return [
        { label: "Mi día", icon: Calendar, path: "/panel/mi-agenda", color: "cyan", tid: "qa-mi-agenda" },
        { label: "Eventos", icon: PartyPopper, path: "/panel/eventos", color: "rose", tid: "qa-eventos" },
      ];
    }
    if (rol === "coordinacion") {
      return [
        { label: "Agenda", icon: Calendar, path: "/panel/agenda", color: "amber", tid: "qa-agenda" },
        { label: "Producción", icon: Sparkles, path: "/panel/produccion", color: "rose", tid: "qa-produccion" },
        { label: "Personal", icon: UserCog, path: "/panel/personal", color: "cyan", tid: "qa-personal" },
        { label: "Inventario", icon: Sparkles, path: "/panel/inventario", color: "purple", tid: "qa-inventario" },
      ];
    }
    // ventas / admin / superadmin
    const base = [
      { label: "Cotizar", icon: Sparkles, path: "/panel/cotizar", color: "rose", tid: "qa-cotizar" },
      { label: "Agenda", icon: Calendar, path: "/panel/agenda", color: "amber", tid: "qa-agenda" },
      { label: "Registrar pago", icon: CreditCard, path: "/panel/pagos", color: "emerald", tid: "qa-pago" },
      { label: "Nuevo evento", icon: Plus, path: "/panel/nuevo-evento", color: "blue", tid: "qa-nuevo-evento" },
    ];
    return base;
  })();

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* ─── HEADER ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-stone-900">Hola, {primerNombre}</h1>
          <p className="text-sm text-stone-600 mt-1">
            {(datasets.eventosHoy || []).length > 0
              ? `Hoy tienes ${datasets.eventosHoy.length} evento${datasets.eventosHoy.length !== 1 ? "s" : ""}. `
              : "Hoy no hay eventos agendados. "}
            {urgentes.length === 0
              ? "Todo está al día."
              : `Tienes ${urgentes.reduce((a, u) => a + u.count, 0)} pendientes esperando atención.`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setConfigOpen(o => !o)} data-testid="btn-config-widgets" style={esRolSoloLectura ? { display: 'none' } : undefined}>
          <Settings className="w-4 h-4 mr-2" /> Personalizar
        </Button>
      </div>

      {/* ─── URGENTES (bloque 1: qué necesito hacer AHORA) ── */}
      {urgentes.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="widget-urgentes">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 mb-3 min-w-0">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-base sm:text-lg text-amber-900">Necesitan tu atención</p>
                <p className="text-xs text-amber-700 break-anywhere">Actúa desde aquí para mantener todo al día</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {urgentes.map(u => (
                <button key={u.tipo} onClick={() => navigate(u.path)}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-400 hover:shadow-sm transition text-left"
                  data-testid={`urgente-${u.tipo}`}>
                  <div>
                    <p className="text-2xl font-display text-amber-900">{u.count}</p>
                    <p className="text-xs text-stone-700">{u.label}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-600" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── ACCIONES RÁPIDAS (bloque 2: crear cosas) ─────── */}
      <Card className="border-stone-200" data-testid="widget-acciones">
        <CardContent className="p-5">
          <p className="text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-3">Acciones rápidas</p>
          <div className={`grid ${accionesRapidas.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"} gap-3`}>
            {accionesRapidas.map(a => {
              const tones = {
                rose:    "bg-rose-50 hover:bg-rose-100 text-rose-900 border-rose-200",
                amber:   "bg-amber-50 hover:bg-amber-100 text-amber-900 border-amber-200",
                emerald: "bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border-emerald-200",
                blue:    "bg-blue-50 hover:bg-blue-100 text-blue-900 border-blue-200",
                cyan:    "bg-cyan-50 hover:bg-cyan-100 text-cyan-900 border-cyan-200",
                purple:  "bg-purple-50 hover:bg-purple-100 text-purple-900 border-purple-200",
              }[a.color] || "bg-stone-50 hover:bg-stone-100 text-stone-900 border-stone-200";
              return (
                <button key={a.label} onClick={() => navigate(a.path)} data-testid={a.tid}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition ${tones}`}>
                  <a.icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{a.label}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ─── HOY / ESTA SEMANA (bloque 3: contexto operativo) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BloqueEventos titulo="Hoy" icon={PartyPopper} eventos={datasets.eventosHoy || []}
          vacio={esRolSoloLectura ? "No tienes eventos asignados hoy" : "No hay eventos hoy · buen día para adelantar cotizaciones"} tid="widget-eventos-hoy" />
        <BloqueEventos titulo="Esta semana" icon={Calendar} eventos={datasets.eventosSemana || []}
          vacio={esRolSoloLectura ? "Sin eventos asignados esta semana" : "Sin eventos esta semana"} tid="widget-eventos-semana" />
      </div>

      {/* ─── CONTEXTO FINANCIERO (bloque 4: solo si aporta) ──── */}
      {isAdmin && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="widget-finanzas-resumen">
          <Card className="border-0" style={{ background: "var(--brand-primary)" }}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-white/80" />
                <p className="text-white/80 text-xs uppercase tracking-wider">Ingresos validados</p>
              </div>
              <p className="font-display text-3xl text-white">{fmt(stats.pagos?.total_recaudado)} <span className="text-sm text-white/70">MXN</span></p>
              <p className="text-white/70 text-xs mt-1">{stats.pagos?.validados || 0} pagos confirmados este período</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-amber-700" />
                <p className="text-amber-800 text-xs uppercase tracking-wider">Por cobrar</p>
              </div>
              <p className="font-display text-3xl text-amber-900">{fmt(stats.pagos?.total_pendiente)} <span className="text-sm text-amber-600">MXN</span></p>
              <p className="text-amber-700 text-xs mt-1">{(stats.pagos?.pendientes || 0) + (stats.pagos?.parciales || 0)} pagos pendientes</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── ENLACE PÚBLICO (bloque 5: crecimiento) ─────────── */}
      {isAdmin && user?.user_id && (
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-stone-500">Comparte tu enlace público de cotización</p>
              {/* En móvil ocultamos la URL larga — solo se muestran los botones */}
              <p className="hidden sm:block text-sm text-stone-700 font-mono truncate">{window.location.origin}/cotizar/{user.user_id}</p>
              <p className="block sm:hidden text-xs text-stone-500">Tu formulario público para recibir solicitudes de cotización.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {navigator.share && (
                <button
                  onClick={() => {
                    navigator.share({
                      title: "Solicita tu cotización",
                      text: "Recibe tu cotización personalizada aquí:",
                      url: `${window.location.origin}/cotizar/${user.user_id}`,
                    }).catch(() => {});
                  }}
                  className="px-3 py-1.5 rounded-full font-medium text-xs text-white flex items-center gap-1"
                  style={{ background: "var(--brand-primary)" }}
                  data-testid="btn-compartir-enlace-publico"
                  aria-label="Compartir enlace de cotización"
                >
                  Compartir cotización
                </button>
              )}
              <button onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/cotizar/${user.user_id}`);
                  toast.success("Enlace copiado");
                }}
                className="px-3 py-1.5 rounded-full font-medium text-xs border"
                style={{ borderColor: "var(--brand-border)", color: "var(--brand-text)" }}
                data-testid="btn-copiar-enlace-publico"
                aria-label="Copiar enlace de cotización"
              >Copiar enlace</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PANEL DE PERSONALIZACIÓN (colapsable) ─────────── */}
      {configOpen && (
        <Card className="border-rose-200 bg-rose-50/40" data-testid="config-widgets-panel">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-base text-rose-900">Widgets extra</h3>
              <Button size="sm" onClick={guardarConfig} data-testid="btn-guardar-config-widgets">Guardar</Button>
            </div>
            <p className="text-xs text-rose-700 mb-3">
              Los bloques principales (urgentes, hoy, semana, finanzas) siempre están visibles.
              Agrega widgets adicionales si necesitas ver más detalle.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableWidgets.filter(w => !["kpis_principales","acciones_rapidas","eventos_proximos"].includes(w.id)).map(w => {
                const active = activeWidgets.includes(w.id);
                return (
                  <button key={w.id} onClick={() => toggleWidget(w.id)} data-testid={`toggle-widget-${w.id}`}
                    className={`text-left p-3 rounded-lg border transition ${active ? "bg-emerald-50 border-emerald-300" : "bg-white border-stone-200 hover:border-rose-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{w.label}</p>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${active ? "bg-emerald-600" : "bg-stone-300"}`}>{active ? "✓" : "+"}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{w.descripcion}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── WIDGETS EXTRA ACTIVADOS ────────────────────────── */}
      {activeWidgets.includes("ocupacion_unidades") && user?.es_celebra_group && (
        <Card className="border-stone-200" data-testid="widget-ocupacion-unidades">
          <CardContent className="p-5">
            <h3 className="font-display text-base text-stone-900 mb-3">Ocupación por unidad (30 días)</h3>
            <div className="grid grid-cols-3 gap-2 text-center">
              <UnitBox label="Petit" tone="emerald" value={contarPorUnidad(datasets.eventosMes, "petit")} />
              <UnitBox label="Kermesita" tone="rose" value={contarPorUnidad(datasets.eventosMes, "kermesita")} />
              <UnitBox label="Bocatto" tone="amber" value={contarPorUnidad(datasets.eventosMes, "bocatto")} />
            </div>
          </CardContent>
        </Card>
      )}

      {activeWidgets.includes("recordatorios_wa") && (datasets.recordatoriosHoy || []).length > 0 && (
        <Card className="border-stone-200" data-testid="widget-recordatorios-hoy">
          <CardContent className="p-0">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-display text-base text-stone-900 flex items-center gap-2">
                <Inbox className="w-4 h-4 text-stone-600" /> Recordatorios de hoy
              </h3>
              <Link to="/panel/whatsapp" className="text-rose-800 text-xs hover:underline">Ver todos</Link>
            </div>
            {(datasets.recordatoriosHoy || []).map(r => (
              <Link key={r.recordatorio_id} to="/panel/whatsapp"
                className="flex items-center justify-between p-3 hover:bg-stone-50 border-b border-stone-100"
                data-testid={`rec-hoy-${r.recordatorio_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-stone-900 truncate text-sm">{r.titulo}</p>
                  <p className="text-xs text-stone-500 truncate">{r.hora || "sin hora"}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{r.prioridad || "media"}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Estado feliz cuando nada urgente Y no hay eventos */}
      {urgentes.length === 0 && (datasets.eventosHoy || []).length === 0 && (datasets.eventosSemana || []).length === 0 && (
        <Card className="border-stone-200 bg-stone-50" data-testid="widget-estado-feliz">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            </div>
            <h3 className="font-display text-xl text-stone-900 mb-1">¡Estás al día!</h3>
            <p className="text-sm text-stone-600 mb-4">No hay eventos ni pendientes urgentes. Es buen momento para vender.</p>
            <Button onClick={() => navigate("/panel/cotizar")} data-testid="btn-cotizar-vacio">
              <Sparkles className="w-4 h-4 mr-2" /> Cotizar un nuevo evento
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const BloqueEventos = ({ titulo, icon: Icon, eventos, vacio, tid }) => (
  <Card className="border-stone-200" data-testid={tid}>
    <CardContent className="p-0">
      <div className="p-4 border-b border-stone-100 flex items-center justify-between">
        <h3 className="font-display text-base text-stone-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-stone-600" /> {titulo}
          {eventos.length > 0 && <span className="text-xs text-stone-500">({eventos.length})</span>}
        </h3>
        <Link to="/panel/eventos" className="text-rose-800 text-xs hover:underline">Ver todos</Link>
      </div>
      {eventos.length === 0 ? (
        <p className="p-6 text-center text-sm text-stone-400">{vacio}</p>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          {eventos.slice(0, 6).map(e => (
            <Link key={e.evento_id} to={`/panel/eventos/${e.evento_id}`}
              className="flex items-center justify-between p-3 hover:bg-stone-50 border-b border-stone-100"
              data-testid={`ev-${e.evento_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-stone-900 truncate text-sm">{e.nombre || e.cliente_nombre}</p>
                <p className="text-xs text-stone-500 truncate">
                  {e.fecha} {e.hora_inicio || ""} · {e.cliente_nombre || "sin cliente"}
                </p>
              </div>
              <StatusBadge status={e.estatus} />
            </Link>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const UnitBox = ({ label, value, tone }) => {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
  }[tone];
  return (
    <div className={`rounded-lg border p-2 sm:p-3 min-w-0 ${tones}`} data-testid={`ocupacion-${label.toLowerCase()}`}>
      <p className="text-[10px] sm:text-xs uppercase tracking-wider opacity-70 truncate">{label}</p>
      <p className="font-display text-xl sm:text-2xl mt-1">{value}</p>
    </div>
  );
};

const contarPorUnidad = (eventos = [], unidad) =>
  (eventos || []).filter(e => (e.unidades || []).includes(unidad)).length;

export default Dashboard;
